import { useSyncExternalStore } from "react";
import {
  listBuddies,
  getTripLogInvites,
  getPendingTripMemberships,
} from "./api";

// Shared "you have new things to look at" counter. Polls the same three
// backends the /account page renders — pending buddy invites, per-species
// log invites, trip-membership invites — so we can badge the Account tab
// with a dot without any single component owning the poll. TripNotifications
// and BuddiesSection continue to fetch on mount for their own render, but
// share this counter so the badge stays in sync with what those sections
// show. Failures are swallowed silently: a badge going momentarily stale
// is fine, and the underlying pages fetch fresh on visit anyway.

interface NotificationState {
  buddyInvites: number;
  logInvites: number;
  tripInvites: number;
  total: number;
}

const EMPTY: NotificationState = {
  buddyInvites: 0,
  logInvites: 0,
  tripInvites: 0,
  total: 0,
};

let state: NotificationState = EMPTY;
const listeners = new Set<() => void>();

function setState(next: NotificationState): void {
  if (
    next.buddyInvites === state.buddyInvites &&
    next.logInvites === state.logInvites &&
    next.tripInvites === state.tripInvites
  ) {
    return;
  }
  state = next;
  for (const l of listeners) l();
}

function subscribe(cb: () => void): () => void {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
}

let inflight: Promise<void> | null = null;
async function refresh(): Promise<void> {
  if (inflight) return inflight;
  inflight = (async () => {
    // Best-effort: three parallel calls; a failing one just leaves that
    // slice at its previous value rather than dropping every count.
    try {
      const [buddies, logs, trips] = await Promise.all([
        listBuddies().catch((e: unknown) => {
          void import("./events").then((m) =>
            m.logEvent("notifications.poll_error", {
              source: "buddies",
              message: e instanceof Error ? e.message : String(e),
            }),
          );
          return null;
        }),
        getTripLogInvites().catch((e: unknown) => {
          void import("./events").then((m) =>
            m.logEvent("notifications.poll_error", {
              source: "log_invites",
              message: e instanceof Error ? e.message : String(e),
            }),
          );
          return null;
        }),
        getPendingTripMemberships().catch((e: unknown) => {
          void import("./events").then((m) =>
            m.logEvent("notifications.poll_error", {
              source: "trip_invites",
              message: e instanceof Error ? e.message : String(e),
            }),
          );
          return null;
        }),
      ]);
      const b = buddies?.invites_received?.length ?? state.buddyInvites;
      const l = logs?.invites?.length ?? state.logInvites;
      const t = trips?.invites?.length ?? state.tripInvites;
      setState({
        buddyInvites: b,
        logInvites: l,
        tripInvites: t,
        total: b + l + t,
      });
    } catch {
      /* silent */
    }
  })();
  try {
    await inflight;
  } finally {
    inflight = null;
  }
}

const POLL_MS = 60_000;
let pollHandle: number | null = null;

/** Start / restart the background poll. Idempotent. */
export function startNotificationPolling(): void {
  if (typeof window === "undefined") return;
  if (pollHandle !== null) return;
  void refresh();
  pollHandle = window.setInterval(() => void refresh(), POLL_MS);
  // A tab returning from background looks stale — fetch on refocus so
  // the badge catches up faster than the 60s polling cadence.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") void refresh();
  });
}

/** Stop polling and clear state — call on sign-out. */
export function stopNotificationPolling(): void {
  if (pollHandle !== null && typeof window !== "undefined") {
    window.clearInterval(pollHandle);
    pollHandle = null;
  }
  setState(EMPTY);
}

/** Manual refresh — call after accepting / dismissing anything. */
export function refreshNotifications(): void {
  void refresh();
}

export function useNotifications(): NotificationState {
  return useSyncExternalStore(
    subscribe,
    () => state,
    () => EMPTY,
  );
}
